import React, { useState, useEffect } from 'react';
import { Mail, RefreshCw, Inbox as InboxIcon, Flag, Paperclip, Eye, Reply } from 'lucide-react';
import emailService from '@/services/emailService';
import toast from 'react-hot-toast';

const Inbox = () => {
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);

  useEffect(() => {
    fetchAccounts();
  }, []);

  useEffect(() => {
    if (selectedAccount) {
      fetchEmails();
    }
  }, [selectedAccount]);

  const fetchAccounts = async () => {
    try {
      const response = await emailService.getAccounts();
      const accountsList = response.data.data || [];
      setAccounts(accountsList);
      if (accountsList.length > 0) {
        setSelectedAccount(accountsList[0].id);
      }
    } catch (error) {
      console.error('Error fetching accounts:', error);
      toast.error('Failed to load email accounts');
    }
  };

  const fetchEmails = async () => {
    if (!selectedAccount) return;
    
    try {
      setLoading(true);
      const response = await emailService.getInbox({ account_id: selectedAccount, limit: 50 });
      setEmails(response.data.data.emails || []);
    } catch (error) {
      console.error('Error fetching emails:', error);
      toast.error('Failed to load emails');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailClick = async (email) => {
    setSelectedEmail(email);
    if (!email.is_read) {
      try {
        await emailService.markAsRead(email.id);
        fetchEmails();
      } catch (error) {
        console.error('Error marking as read:', error);
      }
    }
  };

  return (
    <div className="h-screen flex flex-col">
      <div className="bg-white border-b border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">Inbox</h1>
          <div className="flex items-center space-x-4">
            <select
              value={selectedAccount || ''}
              onChange={(e) => setSelectedAccount(parseInt(e.target.value))}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              {accounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.email_address}
                </option>
              ))}
            </select>
            <button
              onClick={fetchEmails}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Sync
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Email List */}
        <div className="w-1/3 border-r border-gray-200 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : emails.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-500">
              <InboxIcon className="h-12 w-12 mb-4" />
              <p>No emails</p>
            </div>
          ) : (
            <div>
              {emails.map((email) => (
                <div
                  key={email.id}
                  onClick={() => handleEmailClick(email)}
                  className={`p-4 border-b border-gray-200 cursor-pointer hover:bg-gray-50 ${
                    !email.is_read ? 'bg-blue-50' : ''
                  } ${selectedEmail?.id === email.id ? 'bg-blue-100' : ''}`}
                >
                  <div className="flex items-start justify-between mb-1">
                    <span className={`font-medium ${!email.is_read ? 'font-bold' : ''}`}>
                      {email.from_name || email.from_address}
                    </span>
                    <span className="text-xs text-gray-500">
                      {new Date(email.received_at).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="text-sm font-medium text-gray-900 mb-1 truncate">
                    {email.subject || '(No subject)'}
                  </div>
                  <div className="text-sm text-gray-600 truncate">
                    {email.body_text?.substring(0, 100)}...
                  </div>
                  <div className="flex items-center space-x-2 mt-2">
                    {email.has_attachments && (
                      <Paperclip className="h-3 w-3 text-gray-400" />
                    )}
                    {email.is_flagged && (
                      <Flag className="h-3 w-3 text-red-500" />
                    )}
                    {email.erp_reference_id && (
                      <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                        {email.erp_reference_id}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Email Detail */}
        <div className="flex-1 overflow-y-auto bg-white">
          {selectedEmail ? (
            <div className="p-6">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  {selectedEmail.subject || '(No subject)'}
                </h2>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">From:</span>{' '}
                    <span className="text-gray-900">
                      {selectedEmail.from_name ? `${selectedEmail.from_name} <${selectedEmail.from_address}>` : selectedEmail.from_address}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">To:</span>{' '}
                    <span className="text-gray-900">
                      {Array.isArray(selectedEmail.to_addresses) ? selectedEmail.to_addresses.join(', ') : selectedEmail.to_addresses}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Date:</span>{' '}
                    <span className="text-gray-900">
                      {new Date(selectedEmail.received_at).toLocaleString()}
                    </span>
                  </div>
                  {selectedEmail.erp_reference_id && (
                    <div>
                      <span className="font-medium text-gray-700">ERP Reference:</span>{' '}
                      <span className="text-blue-600 font-medium">{selectedEmail.erp_reference_id}</span>
                    </div>
                  )}
                </div>
              </div>

              {selectedEmail.attachments && selectedEmail.attachments.length > 0 && (
                <div className="mb-6">
                  <h3 className="font-medium text-gray-900 mb-2">Attachments:</h3>
                  <div className="space-y-2">
                    {selectedEmail.attachments.map((att) => (
                      <div key={att.id} className="flex items-center space-x-2 text-sm">
                        <Paperclip className="h-4 w-4 text-gray-400" />
                        <span>{att.filename}</span>
                        <span className="text-gray-500">({(att.size_bytes / 1024).toFixed(1)} KB)</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="border-t border-gray-200 pt-6">
                {selectedEmail.body_html ? (
                  <div dangerouslySetInnerHTML={{ __html: selectedEmail.body_html }} />
                ) : (
                  <pre className="whitespace-pre-wrap font-sans text-gray-900">
                    {selectedEmail.body_text}
                  </pre>
                )}
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200">
                <button className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                  <Reply className="h-4 w-4 mr-2" />
                  Reply
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500">
              <div className="text-center">
                <Mail className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                <p>Select an email to view</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Inbox;
